// ===================================================================
// Time & Labor — Supervisor Review
// Dedicated page for supervisors to review and approve time entries
// ===================================================================

import { useEffect, useState, useMemo, useCallback } from 'react';
import { usePolling } from '../../hooks/usePolling';
import {
  Title, Text, Paper, Table, Button, Group, TextInput, Select, Modal,
  SimpleGrid, Badge, Checkbox, Stack, LoadingOverlay, Tabs, Textarea,
  NumberInput, Radio, Divider, Collapse, ActionIcon,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconCalendar, IconCheck, IconX, IconChecklist,
  IconAlertTriangle, IconChevronDown, IconChevronRight,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useTimeLaborStore } from '../../store/timeLaborStore';
import { usePAGrantsStore } from '../../store/paGrantsStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { formatCurrency, stripCurrency } from '../../lib/formatUtils';
import { checkEligibility } from '../../lib/eligibility';
import type { PALaborRecord } from '../../types/pagrants';

const TEAL = '#0D9488';

function pf(v: string | number | undefined): number {
  if (typeof v === 'number') return v || 0;
  return parseFloat(stripCurrency(v || '')) || 0;
}

const STATUS_COLORS: Record<string, string> = {
  draft: 'gray',
  submitted: 'blue',
  approved: 'green',
  rejected: 'red',
};

function StatusBadge({ status }: { status: string }) {
  const color = STATUS_COLORS[status] || 'gray';
  return <Badge size="sm" variant="light" color={color}>{status || 'draft'}</Badge>;
}

/** Extract rejection reason from notes field */
function extractRejectionNotes(notes: string): string {
  const match = notes.match(/^\[REJECTED:\s*(.*?)\]\s*/);
  return match ? match[1] : '';
}

export default function SupervisorReview() {
  const { canEdit } = useModulePermission('timelabor');
  const activeIncident = useAuthStore(s => s.activeIncident);

  const {
    employees, fetchEmployees,
    laborRecords, loadingRecords, fetchLaborRecords,
    updateLaborRecord,
  } = useTimeLaborStore();
  const { projects, fetchProjects } = usePAGrantsStore();

  const [search, setSearch] = useState('');
  const [filterEmployee, setFilterEmployee] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [activeTab, setActiveTab] = useState<string | null>('pending');

  const PAGE_SIZE = 25;
  const [page, setPage] = useState(1);

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);

  // Detail modal
  const [detailRecord, setDetailRecord] = useState<PALaborRecord | null>(null);
  const [detailRejectOpen, setDetailRejectOpen] = useState(false);
  const [detailRejectReason, setDetailRejectReason] = useState('');

  // Split-hour approval controls
  const [approvalMode, setApprovalMode] = useState<string>('approve_all');
  const [customRegHours, setCustomRegHours] = useState<number>(0);
  const [customOTHours, setCustomOTHours] = useState<number>(0);
  const [reviewerNotes, setReviewerNotes] = useState('');

  useEffect(() => {
    fetchLaborRecords();
    fetchProjects();
    fetchEmployees();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  usePolling(() => { fetchLaborRecords(); });

  // Employee profile lookup by name
  const profileByName = useMemo(() => {
    const m = new Map<string, typeof employees[0]>();
    employees.forEach(e => {
      if (e.employeeName) m.set(e.employeeName, e);
    });
    return m;
  }, [employees]);

  // Project lookup
  const projectLookup = useMemo(() => {
    const m = new Map<string, string>();
    projects.forEach(p => m.set(String(p.Rid), [p.projectNumber, p.projectTitle].filter(Boolean).join(' — ') || `#${p.Rid}`));
    return m;
  }, [projects]);

  // Filter by incident
  const incidentRecords = useMemo(() => {
    if (!activeIncident) return [];
    const incId = String(activeIncident.id);
    return laborRecords.filter(r => !r.incidentId || String(r.incidentId) === incId);
  }, [laborRecords, activeIncident]);

  // Employee filter options
  const employeeFilterOptions = useMemo(() => {
    const names = new Set(incidentRecords.map(r => r.employeeName).filter(Boolean));
    return [...names].sort().map(n => ({ value: String(n), label: String(n) }));
  }, [incidentRecords]);

  // Status-based filter
  const statusFilter = useMemo(() => {
    switch (activeTab) {
      case 'pending': return 'submitted';
      case 'approved': return 'approved';
      case 'rejected': return 'rejected';
      default: return null;
    }
  }, [activeTab]);

  // Filtered records
  const filteredRecords = useMemo(() => {
    let list = incidentRecords;
    if (statusFilter) list = list.filter(r => (r.timesheetOnFile || 'draft') === statusFilter);
    if (filterEmployee) list = list.filter(r => r.employeeName === filterEmployee);
    if (dateFrom) list = list.filter(r => r.workDate >= dateFrom);
    if (dateTo) list = list.filter(r => r.workDate <= dateTo);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(r =>
        (r.employeeName || '').toLowerCase().includes(q) ||
        (r.activityDescription || '').toLowerCase().includes(q) ||
        (r.notes || '').toLowerCase().includes(q)
      );
    }
    return [...list].sort((a, b) => (b.workDate || '').localeCompare(a.workDate || '') || (a.employeeName || '').localeCompare(b.employeeName || ''));
  }, [incidentRecords, statusFilter, search, filterEmployee, dateFrom, dateTo]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [filteredRecords.length, activeTab]);

  const totalPages = Math.max(1, Math.ceil(filteredRecords.length / PAGE_SIZE));
  const pageRows = filteredRecords.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // ── Batch grouping for Pending tab ──────────────────────────────
  const [expandedBatches, setExpandedBatches] = useState<Set<string>>(new Set());
  const toggleBatch = useCallback((batchId: string) => {
    setExpandedBatches(prev => {
      const next = new Set(prev);
      if (next.has(batchId)) next.delete(batchId);
      else next.add(batchId);
      return next;
    });
  }, []);

  interface BatchGroup {
    batchId: string;
    records: typeof filteredRecords;
    employeeName: string;
    dateMin: string;
    dateMax: string;
    totalReg: number;
    totalOT: number;
    totalCost: number;
  }

  const batchGroups = useMemo<BatchGroup[]>(() => {
    if (activeTab !== 'pending') return [];
    const grouped = new Map<string, typeof filteredRecords>();
    for (const r of filteredRecords) {
      const batch = String((r as any).timecardBatch || '');
      const bucket = batch || `__unbatched__${r.Rid}`;
      if (!grouped.has(bucket)) grouped.set(bucket, []);
      grouped.get(bucket)!.push(r);
    }
    return [...grouped.entries()].map(([batchId, records]) => {
      const dates = records.map(r => r.workDate).filter(Boolean).sort();
      return {
        batchId,
        records,
        employeeName: records[0]?.employeeName || '',
        dateMin: dates[0] || '',
        dateMax: dates[dates.length - 1] || '',
        totalReg: records.reduce((s, r) => s + pf(r.hoursRegular), 0),
        totalOT: records.reduce((s, r) => s + pf(r.hoursOvertime), 0),
        totalCost: records.reduce((s, r) => s + pf(r.totalCost), 0),
      };
    });
  }, [filteredRecords, activeTab]);

  // Batch approve/reject
  const [batchRejectModalOpen, setBatchRejectModalOpen] = useState(false);
  const [batchRejectTarget, setBatchRejectTarget] = useState<BatchGroup | null>(null);
  const [batchRejectReason, setBatchRejectReason] = useState('');

  const handleBatchApprove = async (group: BatchGroup) => {
    setProcessing(true);
    // Snapshot the records before the loop — batchGroups may shrink as each update triggers fetchRecords
    const toApprove = [...group.records];
    for (const r of toApprove) {
      const loggedReg = pf(r.hoursRegular);
      const loggedOT = pf(r.hoursOvertime);
      const regRate = pf(r.rateRegular);
      const otRate = pf(r.rateOvertime);
      const fringePct = pf(r.fringeBenefitsPct);
      const laborCost = (loggedReg * regRate) + (loggedOT * otRate);
      const fringeCost = laborCost * fringePct / 100;
      // Use updateLaborRecord but skip its internal fetchRecords (we do one final refresh below)
      await updateLaborRecord(r.Rid, {
        timesheetOnFile: 'approved',
        approvedRegHours: String(loggedReg),
        approvedOTHours: String(loggedOT),
        approvedCost: String((laborCost + fringeCost).toFixed(2)),
      });
    }
    // Final authoritative refresh — ensures UI reflects server state after all updates
    await fetchLaborRecords();
    notifications.show({ title: 'Batch Approved', message: `${toApprove.length} entries for ${group.employeeName} approved`, color: 'green' });
    setProcessing(false);
  };

  const handleBatchReject = async () => {
    if (!batchRejectTarget || !batchRejectReason.trim()) return;
    setProcessing(true);
    const toReject = [...batchRejectTarget.records];
    for (const r of toReject) {
      const cleanNotes = (r.notes || '').replace(/^\[REJECTED:.*?\]\s*/, '');
      await updateLaborRecord(r.Rid, {
        timesheetOnFile: 'rejected',
        notes: `[REJECTED: ${batchRejectReason.trim()}] ${cleanNotes}`.trim(),
        reviewerNotes: batchRejectReason.trim(),
      });
    }
    await fetchLaborRecords();
    notifications.show({ title: 'Batch Rejected', message: `${toReject.length} entries rejected`, color: 'red' });
    setBatchRejectTarget(null);
    setBatchRejectModalOpen(false);
    setBatchRejectReason('');
    setProcessing(false);
  };

  // Stats
  const stats = useMemo(() => {
    const pending = incidentRecords.filter(r => (r.timesheetOnFile || 'draft') === 'submitted');
    const approved = incidentRecords.filter(r => r.timesheetOnFile === 'approved');
    const rejected = incidentRecords.filter(r => r.timesheetOnFile === 'rejected');
    const pendingHours = pending.reduce((s, r) => s + pf(r.hoursRegular) + pf(r.hoursOvertime), 0);
    return {
      pendingCount: pending.length,
      approvedCount: approved.length,
      rejectedCount: rejected.length,
      pendingHours,
    };
  }, [incidentRecords]);

  // Toggle selection
  const toggleSelect = useCallback((rid: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(rid)) next.delete(rid);
      else next.add(rid);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    const allSelected = filteredRecords.length > 0 && filteredRecords.every(r => selected.has(r.Rid));
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filteredRecords.map(r => r.Rid)));
    }
  }, [filteredRecords, selected]);

  // Bulk approve
  const handleBulkApprove = async () => {
    if (selected.size === 0) return;
    setProcessing(true);
    let count = 0;
    for (const rid of selected) {
      await updateLaborRecord(rid, { timesheetOnFile: 'approved' });
      count++;
    }
    await fetchLaborRecords();
    notifications.show({ title: 'Approved', message: `${count} time entries approved`, color: 'green' });
    setSelected(new Set());
    setProcessing(false);
  };

  // Bulk reject
  const handleBulkReject = async () => {
    if (selected.size === 0 || !rejectReason.trim()) {
      notifications.show({ title: 'Required', message: 'Please provide a rejection reason', color: 'red' });
      return;
    }
    setProcessing(true);
    let count = 0;
    for (const rid of selected) {
      const rec = laborRecords.find(r => r.Rid === rid);
      const existingNotes = rec?.notes || '';
      // Strip any existing rejection prefix
      const cleanNotes = existingNotes.replace(/^\[REJECTED:.*?\]\s*/, '');
      await updateLaborRecord(rid, {
        timesheetOnFile: 'rejected',
        notes: `[REJECTED: ${rejectReason.trim()}] ${cleanNotes}`.trim(),
      });
      count++;
    }
    notifications.show({ title: 'Rejected', message: `${count} time entries rejected`, color: 'red' });
    setSelected(new Set());
    setRejectReason('');
    setRejectModalOpen(false);
    setProcessing(false);
  };

  // Open detail modal with defaults
  const openDetail = useCallback((rec: PALaborRecord) => {
    setDetailRecord(rec);
    setApprovalMode('approve_all');
    setCustomRegHours(pf(rec.hoursRegular));
    setCustomOTHours(pf(rec.hoursOvertime));
    setReviewerNotes(rec.reviewerNotes || '');
  }, []);

  // Compute approved hours & cost based on current approval mode
  function getApprovedValues(rec: PALaborRecord) {
    const loggedReg = pf(rec.hoursRegular);
    const loggedOT = pf(rec.hoursOvertime);
    const regRate = pf(rec.rateRegular);
    const otRate = pf(rec.rateOvertime);
    const fringePct = pf(rec.fringeBenefitsPct);

    let aReg = loggedReg;
    let aOT = loggedOT;
    switch (approvalMode) {
      case 'approve_all': aReg = loggedReg; aOT = loggedOT; break;
      case 'ot_only': aReg = 0; aOT = loggedOT; break;
      case 'reg_only': aReg = loggedReg; aOT = 0; break;
      case 'custom': aReg = customRegHours; aOT = customOTHours; break;
    }
    const laborCost = (aReg * regRate) + (aOT * otRate);
    const fringeCost = laborCost * fringePct / 100;
    const totalCost = laborCost + fringeCost;
    const isPartial = aReg !== loggedReg || aOT !== loggedOT;
    return { aReg, aOT, laborCost, fringeCost, totalCost, isPartial };
  }

  // Single approve from detail
  const handleDetailApprove = async () => {
    if (!detailRecord) return;
    const { aReg, aOT, totalCost, isPartial } = getApprovedValues(detailRecord);
    if (isPartial && !reviewerNotes.trim()) {
      notifications.show({ title: 'Notes Required', message: 'Please provide reviewer notes explaining the partial approval', color: 'orange' });
      return;
    }
    setProcessing(true);
    await updateLaborRecord(detailRecord.Rid, {
      timesheetOnFile: 'approved',
      approvedRegHours: String(aReg),
      approvedOTHours: String(aOT),
      approvedCost: String(totalCost.toFixed(2)),
      reviewerNotes: reviewerNotes.trim(),
    });
    notifications.show({
      title: 'Approved',
      message: isPartial
        ? `Partial approval for ${detailRecord.employeeName}: ${aReg}h reg + ${aOT}h OT = ${formatCurrency(totalCost.toFixed(2))}`
        : `Time entry for ${detailRecord.employeeName} approved`,
      color: 'green',
    });
    setDetailRecord(null);
    setProcessing(false);
  };

  // Single reject from detail
  const handleDetailReject = async () => {
    if (!detailRecord || !detailRejectReason.trim()) {
      notifications.show({ title: 'Required', message: 'Please provide a rejection reason', color: 'red' });
      return;
    }
    setProcessing(true);
    const existingNotes = detailRecord.notes || '';
    const cleanNotes = existingNotes.replace(/^\[REJECTED:.*?\]\s*/, '');
    await updateLaborRecord(detailRecord.Rid, {
      timesheetOnFile: 'rejected',
      notes: `[REJECTED: ${detailRejectReason.trim()}] ${cleanNotes}`.trim(),
      reviewerNotes: detailRejectReason.trim(),
    });
    notifications.show({ title: 'Rejected', message: `Time entry for ${detailRecord.employeeName} rejected`, color: 'red' });
    setDetailRecord(null);
    setDetailRejectOpen(false);
    setDetailRejectReason('');
    setProcessing(false);
  };

  return (
    <div style={{ position: 'relative' }}>
      <LoadingOverlay visible={(loadingRecords && laborRecords.length === 0) || processing} />

      <Group justify="space-between" mb="md">
        <div>
          <Title order={3}>Supervisor Review</Title>
          <Text size="sm" c="dimmed">Review and approve submitted time entries</Text>
        </div>
      </Group>

      {/* Stat Cards */}
      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} mb="lg">
        <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #2563EB' }}>
          <IconChecklist size={20} color="#2563EB" />
          <Text size="xl" fw={700}>{stats.pendingCount}</Text>
          <Text size="xs" c="dimmed">Pending Review</Text>
        </Paper>
        <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #059669' }}>
          <IconCheck size={20} color="#059669" />
          <Text size="xl" fw={700}>{stats.approvedCount}</Text>
          <Text size="xs" c="dimmed">Approved</Text>
        </Paper>
        <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #DC2626' }}>
          <IconX size={20} color="#DC2626" />
          <Text size="xl" fw={700}>{stats.rejectedCount}</Text>
          <Text size="xs" c="dimmed">Rejected</Text>
        </Paper>
        <Paper p="sm" withBorder ta="center" style={{ borderTop: `3px solid ${TEAL}` }}>
          <Text size="xl" fw={700}>{stats.pendingHours.toFixed(1)}</Text>
          <Text size="xs" c="dimmed">Total Hours Pending</Text>
        </Paper>
      </SimpleGrid>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={v => { setActiveTab(v); setSelected(new Set()); }} mb="md">
        <Tabs.List>
          <Tabs.Tab value="pending">
            Pending {stats.pendingCount > 0 && <Badge size="xs" ml={4} variant="filled" color="blue">{stats.pendingCount}</Badge>}
          </Tabs.Tab>
          <Tabs.Tab value="approved">Approved</Tabs.Tab>
          <Tabs.Tab value="rejected">Rejected</Tabs.Tab>
          <Tabs.Tab value="all">All</Tabs.Tab>
        </Tabs.List>
      </Tabs>

      {/* Filters */}
      <Group gap={8} mb="md">
        <TextInput size="xs" placeholder="Search..." leftSection={<IconSearch size={14} />}
          value={search} onChange={e => setSearch(e.target.value)} style={{ maxWidth: 200 }} />
        <Select size="xs" placeholder="Employee" data={employeeFilterOptions} value={filterEmployee}
          onChange={setFilterEmployee} clearable searchable style={{ width: 180 }} />
        <TextInput size="xs" type="date" value={dateFrom}
          onChange={e => setDateFrom(e.target.value)} style={{ width: 140 }}
          leftSection={<IconCalendar size={14} />} />
        <TextInput size="xs" type="date" value={dateTo}
          onChange={e => setDateTo(e.target.value)} style={{ width: 140 }} />
      </Group>

      {/* Bulk actions bar */}
      {canEdit && selected.size > 0 && (activeTab === 'pending' || activeTab === 'all') && (
        <Paper p="xs" withBorder mb="md" bg="blue.0">
          <Group justify="space-between">
            <Text size="sm" fw={500}>{selected.size} selected</Text>
            <Group gap={8}>
              <Button size="xs" color="green" leftSection={<IconCheck size={14} />}
                onClick={handleBulkApprove} loading={processing}>
                Approve Selected
              </Button>
              <Button size="xs" color="red" leftSection={<IconX size={14} />}
                onClick={() => setRejectModalOpen(true)}>
                Reject Selected
              </Button>
            </Group>
          </Group>
        </Paper>
      )}

      {/* ── Pending tab: batch-grouped view ── */}
      {activeTab === 'pending' && (
        <Stack gap="sm">
          {batchGroups.length === 0 && (
            <Paper withBorder p="lg">
              <Text size="sm" c="dimmed" ta="center">No pending time entries.</Text>
            </Paper>
          )}
          {batchGroups.map(group => {
            const isBatched = !group.batchId.startsWith('__unbatched__');
            const isExpanded = expandedBatches.has(group.batchId);
            const dateLabel = group.dateMin === group.dateMax
              ? group.dateMin
              : `${group.dateMin} → ${group.dateMax}`;
            return (
              <Paper key={group.batchId} withBorder style={{ overflow: 'hidden' }}>
                {/* Batch header */}
                <Group
                  p="sm"
                  justify="space-between"
                  style={{ cursor: 'pointer', background: '#f8f9fa' }}
                  onClick={() => toggleBatch(group.batchId)}
                >
                  <Group gap={8}>
                    <ActionIcon size="xs" variant="subtle">
                      {isExpanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                    </ActionIcon>
                    <div>
                      <Group gap={6}>
                        <Text fw={600} size="sm">{group.employeeName}</Text>
                        {isBatched && <Badge size="xs" variant="light" color="teal">{group.records.length} entries</Badge>}
                        {!isBatched && <Badge size="xs" variant="light" color="gray">1 entry</Badge>}
                      </Group>
                      <Text size="xs" c="dimmed">{dateLabel}</Text>
                    </div>
                  </Group>
                  <Group gap={16} align="center">
                    <div style={{ textAlign: 'right' }}>
                      <Text size="xs" c="dimmed">Hours</Text>
                      <Text size="sm" fw={600}>{group.totalReg.toFixed(1)} reg{group.totalOT > 0 ? ` + ${group.totalOT.toFixed(1)} OT` : ''}</Text>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <Text size="xs" c="dimmed">Total Cost</Text>
                      <Text size="sm" fw={600}>{formatCurrency(group.totalCost.toFixed(2))}</Text>
                    </div>
                    {canEdit && (
                      <Group gap={6} onClick={e => e.stopPropagation()}>
                        <Button size="xs" color="green" leftSection={<IconCheck size={12} />}
                          loading={processing} onClick={() => handleBatchApprove(group)}>
                          Approve
                        </Button>
                        <Button size="xs" color="red" variant="light" leftSection={<IconX size={12} />}
                          onClick={() => { setBatchRejectTarget(group); setBatchRejectReason(''); setBatchRejectModalOpen(true); }}>
                          Reject
                        </Button>
                      </Group>
                    )}
                  </Group>
                </Group>
                {/* Expanded rows */}
                <Collapse in={isExpanded}>
                  <Table striped size="xs">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Date</Table.Th>
                        <Table.Th>Activity</Table.Th>
                        <Table.Th>Category</Table.Th>
                        <Table.Th>Site</Table.Th>
                        <Table.Th style={{ textAlign: 'right' }}>Reg</Table.Th>
                        <Table.Th style={{ textAlign: 'right' }}>OT</Table.Th>
                        <Table.Th style={{ textAlign: 'right' }}>Total $</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {group.records.map(r => (
                        <Table.Tr key={r.Rid} style={{ cursor: 'pointer' }} onClick={() => openDetail(r)}>
                          <Table.Td>{r.workDate}</Table.Td>
                          <Table.Td style={{ maxWidth: 200 }}><Text size="xs" truncate>{r.activityDescription || '-'}</Text></Table.Td>
                          <Table.Td>{r.category ? <Badge size="xs" variant="light" color="gray">Cat {r.category}</Badge> : '-'}</Table.Td>
                          <Table.Td style={{ maxWidth: 120 }}><Text size="xs" c="dimmed" truncate>{r.notes?.replace(/^\[REJECTED:.*?\]\s*/, '') || '-'}</Text></Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>{pf(r.hoursRegular).toFixed(1)}</Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>{pf(r.hoursOvertime) > 0 ? pf(r.hoursOvertime).toFixed(1) : '-'}</Table.Td>
                          <Table.Td style={{ textAlign: 'right' }} fw={600}>{formatCurrency(r.totalCost)}</Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Collapse>
              </Paper>
            );
          })}
        </Stack>
      )}

      {/* ── Non-pending tabs: flat table view ── */}
      {activeTab !== 'pending' && (
      <Paper withBorder>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              {canEdit && activeTab === 'all' && (
                <Table.Th w={40}>
                  <Checkbox
                    size="xs"
                    checked={filteredRecords.length > 0 && filteredRecords.every(r => selected.has(r.Rid))}
                    indeterminate={filteredRecords.some(r => selected.has(r.Rid)) && !filteredRecords.every(r => selected.has(r.Rid))}
                    onChange={toggleAll}
                  />
                </Table.Th>
              )}
              <Table.Th>Date</Table.Th>
              <Table.Th>Employee</Table.Th>
              <Table.Th>Title</Table.Th>
              <Table.Th>Activity</Table.Th>
              <Table.Th>Category</Table.Th>
              <Table.Th>Site</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Reg Hrs</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>OT Hrs</Table.Th>
              {activeTab === 'approved' && <Table.Th style={{ textAlign: 'right' }}>Appr Reg</Table.Th>}
              {activeTab === 'approved' && <Table.Th style={{ textAlign: 'right' }}>Appr OT</Table.Th>}
              <Table.Th style={{ textAlign: 'right' }}>Rate</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>{activeTab === 'approved' ? 'Appr $' : 'Total $'}</Table.Th>
              <Table.Th>Status</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filteredRecords.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={13}>
                  <Text size="sm" c="dimmed" ta="center" py="lg">No time entries found.</Text>
                </Table.Td>
              </Table.Tr>
            ) : pageRows.map(r => {
              const status = r.timesheetOnFile || 'draft';
              return (
                <Table.Tr key={r.Rid} style={{ cursor: 'pointer' }} onClick={() => openDetail(r)}>
                  {canEdit && activeTab === 'all' && (
                    <Table.Td onClick={e => e.stopPropagation()}>
                      <Checkbox size="xs" checked={selected.has(r.Rid)} onChange={() => toggleSelect(r.Rid)} />
                    </Table.Td>
                  )}
                  <Table.Td>{r.workDate}</Table.Td>
                  <Table.Td fw={500}>{r.employeeName}</Table.Td>
                  <Table.Td><Text size="sm" c="dimmed">{r.employeeTitle || '-'}</Text></Table.Td>
                  <Table.Td style={{ maxWidth: 180 }}>
                    <Text size="sm" truncate>{r.activityDescription || '-'}</Text>
                  </Table.Td>
                  <Table.Td>
                    {r.category ? <Badge size="xs" variant="light" color="gray">Cat {r.category}</Badge> : '-'}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed" truncate style={{ maxWidth: 100 }}>
                      {r.notes?.replace(/^\[REJECTED:.*?\]\s*/, '') || '-'}
                    </Text>
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{pf(r.hoursRegular).toFixed(1)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{pf(r.hoursOvertime) > 0 ? pf(r.hoursOvertime).toFixed(1) : '-'}</Table.Td>
                  {activeTab === 'approved' && (
                    <Table.Td style={{ textAlign: 'right' }}>
                      {r.approvedRegHours ? (
                        <Text size="sm" fw={500} c={pf(r.approvedRegHours) < pf(r.hoursRegular) ? 'orange' : undefined}>
                          {pf(r.approvedRegHours).toFixed(1)}
                        </Text>
                      ) : pf(r.hoursRegular).toFixed(1)}
                    </Table.Td>
                  )}
                  {activeTab === 'approved' && (
                    <Table.Td style={{ textAlign: 'right' }}>
                      {r.approvedOTHours ? (
                        <Text size="sm" fw={500} c={pf(r.approvedOTHours) < pf(r.hoursOvertime) ? 'orange' : undefined}>
                          {pf(r.approvedOTHours).toFixed(1)}
                        </Text>
                      ) : (pf(r.hoursOvertime) > 0 ? pf(r.hoursOvertime).toFixed(1) : '-')}
                    </Table.Td>
                  )}
                  <Table.Td style={{ textAlign: 'right' }}>{formatCurrency(r.rateRegular)}/hr</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }} fw={600}>
                    {activeTab === 'approved' && r.approvedCost
                      ? formatCurrency(r.approvedCost)
                      : formatCurrency(r.totalCost)}
                  </Table.Td>
                  <Table.Td><StatusBadge status={status} /></Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Paper>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Group justify="center" mt="sm" gap="xs">
          <Button size="xs" variant="subtle" disabled={page === 1} onClick={() => setPage(1)}>««</Button>
          <Button size="xs" variant="subtle" disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))}>‹ Prev</Button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const start = Math.max(1, Math.min(page - 2, totalPages - 4));
            const p2 = start + i;
            return p2 <= totalPages ? (
              <Button key={p2} size="xs" variant={p2 === page ? 'filled' : 'subtle'} onClick={() => setPage(p2)}>{p2}</Button>
            ) : null;
          })}
          <Button size="xs" variant="subtle" disabled={page === totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next ›</Button>
          <Button size="xs" variant="subtle" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»»</Button>
          <Text size="xs" c="dimmed" ml="xs">{filteredRecords.length} total</Text>
        </Group>
      )}

      {/* Detail Modal */}
      <Modal opened={!!detailRecord} onClose={() => setDetailRecord(null)} title="Time Entry Review" size="lg">
        {detailRecord && (() => {
          const status = detailRecord.timesheetOnFile || 'draft';
          const rejectionNotes = extractRejectionNotes(detailRecord.notes || '');
          const cleanNotes = (detailRecord.notes || '').replace(/^\[REJECTED:.*?\]\s*/, '');
          const profile = profileByName.get(detailRecord.employeeName);
          const loggedReg = pf(detailRecord.hoursRegular);
          const loggedOT = pf(detailRecord.hoursOvertime);
          const av = getApprovedValues(detailRecord);

          // Eligibility flags
          const eligFlags = profile ? checkEligibility(
            profile.budgetStatus, profile.flsaStatus, profile.jobTitle || detailRecord.employeeTitle,
            detailRecord.category || '', loggedReg, loggedOT,
          ) : [];

          return (
            <Stack gap="md">
              {/* Status */}
              <Group justify="space-between">
                <StatusBadge status={status} />
                {detailRecord.projectId && (
                  <Badge variant="light" color={TEAL}>
                    Project: {projectLookup.get(detailRecord.projectId) || detailRecord.projectId}
                  </Badge>
                )}
              </Group>

              {/* Rejection notes */}
              {status === 'rejected' && rejectionNotes && (
                <Paper p="sm" withBorder style={{ borderLeft: '3px solid #DC2626' }} bg="red.0">
                  <Text size="xs" fw={600} c="red.8" mb={4}>Rejection Reason</Text>
                  <Text size="sm">{rejectionNotes}</Text>
                </Paper>
              )}

              {/* Employee & Date */}
              <SimpleGrid cols={2}>
                <div>
                  <Text size="xs" c="dimmed">Employee</Text>
                  <Text fw={600}>{detailRecord.employeeName}</Text>
                  <Text size="sm" c="dimmed">{detailRecord.employeeTitle || '-'}</Text>
                  {detailRecord.employeeId && <Text size="xs" c="dimmed">ID: {detailRecord.employeeId}</Text>}
                </div>
                <div>
                  <Text size="xs" c="dimmed">Date</Text>
                  <Text fw={600}>{detailRecord.workDate}</Text>
                </div>
              </SimpleGrid>

              {/* Activity & Site */}
              <SimpleGrid cols={2}>
                <div>
                  <Text size="xs" c="dimmed">Activity / Task</Text>
                  <Group gap={6}>
                    <Text fw={500}>{detailRecord.activityDescription || '-'}</Text>
                    {detailRecord.category && (
                      <Badge size="xs" variant="light" color="gray">Cat {detailRecord.category}</Badge>
                    )}
                  </Group>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Site / Location</Text>
                  <Text>{cleanNotes || '-'}</Text>
                </div>
              </SimpleGrid>

              {/* Logged Hours & Rates */}
              <Paper p="sm" withBorder>
                <Text size="xs" fw={600} mb="xs" c="dimmed" tt="uppercase">Logged Hours & Cost</Text>
                <SimpleGrid cols={3}>
                  <div style={{ textAlign: 'center' }}>
                    <Text size="xs" c="dimmed">Regular Hours</Text>
                    <Text size="lg" fw={700}>{loggedReg.toFixed(1)}</Text>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <Text size="xs" c="dimmed">Overtime Hours</Text>
                    <Text size="lg" fw={700}>{loggedOT > 0 ? loggedOT.toFixed(1) : '-'}</Text>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <Text size="xs" c="dimmed">Total Hours</Text>
                    <Text size="lg" fw={700}>{(loggedReg + loggedOT).toFixed(1)}</Text>
                  </div>
                </SimpleGrid>
                <Divider my="xs" />
                <SimpleGrid cols={4}>
                  <div>
                    <Text size="xs" c="dimmed">Reg Rate</Text>
                    <Text fw={500}>{formatCurrency(detailRecord.rateRegular)}/hr</Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed">OT Rate</Text>
                    <Text fw={500}>{pf(detailRecord.rateOvertime) > 0 ? formatCurrency(detailRecord.rateOvertime) + '/hr' : 'N/A'}</Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed">Fringe %</Text>
                    <Text fw={500}>{detailRecord.fringeBenefitsPct || '0'}%</Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed">Logged Total Cost</Text>
                    <Text fw={700} c={TEAL}>{formatCurrency(detailRecord.totalCost)}</Text>
                  </div>
                </SimpleGrid>
              </Paper>

              {/* Employee Classification */}
              {profile && (
                <Paper p="sm" withBorder>
                  <Text size="xs" fw={600} mb="xs" c="dimmed" tt="uppercase">Employee Classification</Text>
                  <SimpleGrid cols={3}>
                    <div>
                      <Text size="xs" c="dimmed">Employment Type</Text>
                      <Badge size="sm" variant="light">{profile.employmentType?.replace(/_/g, ' ') || '-'}</Badge>
                    </div>
                    <div>
                      <Text size="xs" c="dimmed">FLSA Status</Text>
                      <Badge size="sm" variant="light" color={profile.flsaStatus === 'exempt' ? 'orange' : 'blue'}>
                        {profile.flsaStatus === 'exempt' ? 'Exempt' : 'Non-Exempt'}
                      </Badge>
                    </div>
                    <div>
                      <Text size="xs" c="dimmed">Budget Status</Text>
                      <Badge size="sm" variant="light" color={profile.budgetStatus === 'budgeted' ? 'green' : 'gray'}>
                        {profile.budgetStatus === 'budgeted' ? 'Budgeted' : 'Unbudgeted'}
                      </Badge>
                    </div>
                  </SimpleGrid>
                </Paper>
              )}

              {/* Eligibility Flags */}
              {eligFlags.length > 0 && (
                <Paper p="sm" withBorder style={{ borderLeft: '3px solid #F59E0B' }}>
                  <Text size="xs" fw={600} mb="xs" c="orange.8">PAPPG Eligibility Notes</Text>
                  {eligFlags.map((f, i) => (
                    <Group key={i} gap="xs" mb={4}>
                      <IconAlertTriangle size={14} color="#F59E0B" />
                      <Text size="sm">{f.message}</Text>
                      <Text size="xs" c="dimmed">({f.rule})</Text>
                    </Group>
                  ))}
                </Paper>
              )}

              {/* Approval Controls — only for submitted entries */}
              {canEdit && status === 'submitted' && (
                <>
                  <Divider label="Approval Decision" labelPosition="center" />

                  <Radio.Group value={approvalMode} onChange={setApprovalMode} label="Hour Approval">
                    <Stack gap={6} mt={4}>
                      <Radio value="approve_all" label={`Approve All Hours (${loggedReg.toFixed(1)} reg + ${loggedOT.toFixed(1)} OT)`} />
                      {loggedOT > 0 && (
                        <Radio value="ot_only" label={`Approve OT Only (${loggedOT.toFixed(1)} OT, 0 reg)`} />
                      )}
                      {loggedReg > 0 && (
                        <Radio value="reg_only" label={`Approve Regular Only (${loggedReg.toFixed(1)} reg, 0 OT)`} />
                      )}
                      <Radio value="custom" label="Custom Split" />
                    </Stack>
                  </Radio.Group>

                  {approvalMode === 'ot_only' && (
                    <Paper p="xs" withBorder bg="blue.0">
                      <Text size="xs" c="blue.8">Straight time held back -- not sent to PA project. Only overtime hours will be included in cost calculations.</Text>
                    </Paper>
                  )}

                  {approvalMode === 'custom' && (
                    <SimpleGrid cols={2}>
                      <NumberInput
                        label="Approved Regular Hours"
                        size="sm"
                        min={0}
                        max={loggedReg}
                        decimalScale={1}
                        value={customRegHours}
                        onChange={v => setCustomRegHours(Number(v) || 0)}
                      />
                      <NumberInput
                        label="Approved OT Hours"
                        size="sm"
                        min={0}
                        max={loggedOT}
                        decimalScale={1}
                        value={customOTHours}
                        onChange={v => setCustomOTHours(Number(v) || 0)}
                      />
                    </SimpleGrid>
                  )}

                  {/* Approved cost comparison */}
                  <Paper p="sm" withBorder bg={av.isPartial ? 'orange.0' : 'green.0'}>
                    <SimpleGrid cols={3}>
                      <div style={{ textAlign: 'center' }}>
                        <Text size="xs" c="dimmed">Approved Hours</Text>
                        <Text fw={700}>{av.aReg.toFixed(1)} reg + {av.aOT.toFixed(1)} OT</Text>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <Text size="xs" c="dimmed">Approved Cost</Text>
                        <Text fw={700} c={av.isPartial ? 'orange.8' : 'green.8'}>
                          {formatCurrency(av.totalCost.toFixed(2))}
                        </Text>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <Text size="xs" c="dimmed">Difference</Text>
                        <Text fw={600} c={av.isPartial ? 'red' : 'dimmed'}>
                          {av.isPartial
                            ? `- ${formatCurrency((pf(detailRecord.totalCost) - av.totalCost).toFixed(2))}`
                            : 'None'}
                        </Text>
                      </div>
                    </SimpleGrid>
                  </Paper>

                  {/* Reviewer notes */}
                  <Textarea
                    label={av.isPartial ? 'Reviewer Notes (required for partial approval)' : 'Reviewer Notes'}
                    placeholder="Reason for approval decision, PAPPG citation, etc."
                    value={reviewerNotes}
                    onChange={e => setReviewerNotes(e.target.value)}
                    minRows={2}
                  />
                </>
              )}

              {/* Previously approved details */}
              {status === 'approved' && detailRecord.approvedRegHours && (
                <Paper p="sm" withBorder style={{ borderLeft: `3px solid ${TEAL}` }}>
                  <Text size="xs" fw={600} mb="xs" c="dimmed" tt="uppercase">Approved Hours</Text>
                  <SimpleGrid cols={3}>
                    <div>
                      <Text size="xs" c="dimmed">Approved Reg</Text>
                      <Text fw={700} c={pf(detailRecord.approvedRegHours) < loggedReg ? 'orange' : undefined}>
                        {pf(detailRecord.approvedRegHours).toFixed(1)}
                      </Text>
                    </div>
                    <div>
                      <Text size="xs" c="dimmed">Approved OT</Text>
                      <Text fw={700} c={pf(detailRecord.approvedOTHours) < loggedOT ? 'orange' : undefined}>
                        {pf(detailRecord.approvedOTHours).toFixed(1)}
                      </Text>
                    </div>
                    <div>
                      <Text size="xs" c="dimmed">Approved Cost</Text>
                      <Text fw={700} c={TEAL}>{formatCurrency(detailRecord.approvedCost)}</Text>
                    </div>
                  </SimpleGrid>
                  {detailRecord.reviewerNotes && (
                    <>
                      <Divider my="xs" />
                      <Text size="xs" c="dimmed">Reviewer Notes</Text>
                      <Text size="sm">{detailRecord.reviewerNotes}</Text>
                    </>
                  )}
                </Paper>
              )}

              {/* Actions */}
              <Group justify="flex-end" gap="sm" style={{ borderTop: '1px solid var(--mantine-color-gray-2)', paddingTop: 12 }}>
                <Button variant="light" color="gray" onClick={() => setDetailRecord(null)}>Cancel</Button>
                {canEdit && status === 'submitted' && (
                  <>
                    <Button color="red" variant="light" leftSection={<IconX size={14} />}
                      onClick={() => setDetailRejectOpen(true)}>
                      Reject
                    </Button>
                    <Button color="green" leftSection={<IconCheck size={14} />}
                      onClick={handleDetailApprove} loading={processing}>
                      Approve{av.isPartial ? ' (Partial)' : ''}
                    </Button>
                  </>
                )}
              </Group>
            </Stack>
          );
        })()}
      </Modal>

      {/* Bulk Reject Modal */}
      <Modal opened={rejectModalOpen} onClose={() => setRejectModalOpen(false)} title="Reject Selected Entries" size="sm">
        <Stack gap="sm">
          <Text size="sm">Rejecting {selected.size} time entries. Please provide a reason:</Text>
          <Textarea
            placeholder="Reason for rejection..."
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            minRows={3}
          />
          <Group justify="flex-end">
            <Button variant="light" color="gray" onClick={() => setRejectModalOpen(false)}>Cancel</Button>
            <Button color="red" onClick={handleBulkReject} loading={processing}
              disabled={!rejectReason.trim()}>
              Reject {selected.size} Entries
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Batch Reject Modal */}
      <Modal opened={batchRejectModalOpen} onClose={() => setBatchRejectModalOpen(false)} title="Reject Timecard Batch" size="sm">
        <Stack gap="sm">
          {batchRejectTarget && (
            <Text size="sm">
              Rejecting <strong>{batchRejectTarget.records.length}</strong> entries for <strong>{batchRejectTarget.employeeName}</strong>.
            </Text>
          )}
          <Textarea
            placeholder="Reason for rejection..."
            value={batchRejectReason}
            onChange={e => setBatchRejectReason(e.target.value)}
            minRows={3}
          />
          <Group justify="flex-end">
            <Button variant="light" color="gray" onClick={() => setBatchRejectModalOpen(false)}>Cancel</Button>
            <Button color="red" onClick={handleBatchReject} loading={processing}
              disabled={!batchRejectReason.trim()}>
              Reject All
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Detail Reject Modal */}
      <Modal opened={detailRejectOpen} onClose={() => setDetailRejectOpen(false)} title="Reject Time Entry" size="sm">
        <Stack gap="sm">
          <Text size="sm">Rejecting entry for <strong>{detailRecord?.employeeName}</strong> on {detailRecord?.workDate}.</Text>
          <Textarea
            placeholder="Reason for rejection..."
            value={detailRejectReason}
            onChange={e => setDetailRejectReason(e.target.value)}
            minRows={3}
          />
          <Group justify="flex-end">
            <Button variant="light" color="gray" onClick={() => setDetailRejectOpen(false)}>Cancel</Button>
            <Button color="red" onClick={handleDetailReject} loading={processing}
              disabled={!detailRejectReason.trim()}>
              Reject
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
